import React from 'react';
import type { PaginationMeta } from '@yawi/domain';
import { Pagination } from '../Pagination';
import { DataTableHeader } from './DataTableHeader';
import { DataTableEmpty } from './DataTableEmpty';
import { DataTableLoading } from './DataTableLoading';

export interface ColumnDef<T> {
  id: string;
  header: string;
  accessorKey?: keyof T;
  cell?: (item: T) => React.ReactNode;
  className?: string;
}

interface DataTableProps<T> {
  // Data
  data: T[];
  columns: ColumnDef<T>[];
  keyExtractor: (item: T) => string;

  // Pagination
  meta?: PaginationMeta | null;
  onPageChange?: (page: number) => void;
  pageSize?: number;
  onPageSizeChange?: (size: number) => void;

  // Header
  title?: string;

  // Search
  searchPlaceholder?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;

  // Filters
  filters?: React.ReactNode;

  // Actions
  actions?: React.ReactNode;

  // States
  loading?: boolean;
  emptyMessage?: string;
  emptyIcon?: React.ReactNode;

  // Row interactions
  onRowClick?: (item: T) => void;
  rowActions?: (item: T) => React.ReactNode;

  // Layout
  minHeight?: string;
}

export function DataTable<T>({
  data,
  columns,
  keyExtractor,
  meta,
  onPageChange,
  pageSize,
  onPageSizeChange,
  title,
  searchPlaceholder,
  searchValue = '',
  onSearchChange,
  filters,
  actions,
  loading = false,
  emptyMessage,
  emptyIcon,
  onRowClick,
  rowActions,
  minHeight = 'min-h-[500px]',
}: DataTableProps<T>) {
  const getCellValue = (item: T, column: ColumnDef<T>) => {
    if (column.cell) {
      return column.cell(item);
    }
    if (column.accessorKey) {
      const value = item[column.accessorKey];
      return value !== null && value !== undefined ? String(value) : '-';
    }
    return '-';
  };

  return (
    <div className={`bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 overflow-hidden flex flex-col ${minHeight}`}>
      {/* Header with search and filters */}
      {onSearchChange && (
        <DataTableHeader
          title={title}
          searchValue={searchValue}
          onSearchChange={onSearchChange}
          searchPlaceholder={searchPlaceholder}
          filters={filters}
          actions={actions}
        />
      )}

      {/* Table */}
      <div className="overflow-x-auto flex-1">
        <table className="w-full text-sm text-left">
          <thead className="bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 font-medium sticky top-0">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.id}
                  className={`p-4 ${column.className || ''}`}
                >
                  {column.header}
                </th>
              ))}
              {rowActions && (
                <th className="p-4 text-right">Actions</th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {loading ? (
              <tr>
                <td colSpan={columns.length + (rowActions ? 1 : 0)}>
                  <DataTableLoading rows={5} columns={columns.length} />
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (rowActions ? 1 : 0)}>
                  <DataTableEmpty message={emptyMessage} icon={emptyIcon} />
                </td>
              </tr>
            ) : (
              data.map((item) => (
                <tr
                  key={keyExtractor(item)}
                  className={`hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors ${onRowClick ? 'cursor-pointer' : ''}`}
                  onClick={() => onRowClick?.(item)}
                >
                  {columns.map((column) => (
                    <td
                      key={column.id}
                      className={`p-4 text-gray-800 dark:text-gray-200 ${column.className || ''}`}
                    >
                      {getCellValue(item, column)}
                    </td>
                  ))}
                  {rowActions && (
                    <td className="p-4 text-right">
                      <div
                        className="flex items-center justify-end gap-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {rowActions(item)}
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {meta && onPageChange && (
        <Pagination
          meta={meta}
          onPageChange={onPageChange}
          pageSize={pageSize}
          onPageSizeChange={onPageSizeChange}
          loading={loading}
        />
      )}
    </div>
  );
}
